import React, {useCallback, useMemo, useReducer, useRef, useState} from "react";
import {
  CreateNewProjectAction,
  getTotalBilledMinutes,
  ICreateNewProjectState,
  initialCreateNewProjectState,
  newProjectReducer
} from "../../reducers/createNewProjectReducer.ts";
import {useSteps} from "@chakra-ui/react";
import iso6391 from "iso-639-1";
import {CREATE_NEW_PROJECT_STEPS} from "../../models/constants/constants.ts";
import CreateProjectSteps from "./CreateProjectSteps.tsx";
import StepUploadFiles from "./SetupUploadFiles.tsx";
import CreateProjectModalFooter from "./CreateProjectModalFooter.tsx";
import LanguagesStep from "./LanguagesStep.tsx";
import type {SubtitleStep} from "./LanguagesStep.tsx";
import {isLanguagesStepValid} from "./languageStepValidation.ts";
import {ILanguageConfiguration} from "../../models/interfaces/languageConfiguration.interface.ts";
import {getFileBaseName} from "../../utils/utils.ts";
import {DEFAULT_CREDIT_RATES} from "../../utils/creditsCost.ts";
import {buildCreditBreakdown, ICreditBreakdown} from "../../utils/creditsBreakdown.ts";
import {SanitizedPreset} from "../../utils/presetSanitizer.ts";
import {useProjectsStore} from "../../store/useProjectsStore.ts";
import {useCreditsStore} from "../../store/useCreditsStore.ts";
import useUserStore from "../../store/useUserStore.ts";
import {IProject} from "../../models/interfaces/project.interface.ts";
import type {ICreditQuote} from "../../models/interfaces/credits.interface.ts";

const UPLOAD_STEP = 0;
const LANGUAGES_STEP = 1;

const DUPLICATE_NAME_ERROR = "Project name already exists";
const EMPTY_NAME_ERROR = "Project name cannot be empty";

interface CreateNewProjectModalProps {
  createNewProject: (state: ICreateNewProjectState) => Promise<void>;
}

const CreateNewProjectModal: React.FC<CreateNewProjectModalProps> = (
  {
    createNewProject,
  }: CreateNewProjectModalProps) => {
  const [state, dispatch] = useReducer<ICreateNewProjectState, [action: CreateNewProjectAction]>(newProjectReducer, initialCreateNewProjectState);
  const [subtitleInitialStep, setSubtitleInitialStep] = useState<SubtitleStep>("source");
  const stepScrollAreaRef = useRef<HTMLDivElement>(null);
  const {projects} = useProjectsStore();
  const balance = useCreditsStore((credits) => credits.balance);
  const catalog = useCreditsStore((credits) => credits.catalog);
  const fetchBalance = useCreditsStore((credits) => credits.fetchBalance);
  const openNotEnough = useCreditsStore((credits) => credits.openNotEnough);
  const workspaceId: string | undefined = useUserStore((user) => user.user?.workspaceId);
  const [isCheckingCredits, setIsCheckingCredits] = useState<boolean>(false);
  const isCheckingCreditsRef = useRef<boolean>(false);
  // `goToNext` is deliberately unused: it clamps to `count`, i.e. one past the last step, which
  // renders an empty modal body. Every jump goes through `setActiveStep` with an explicit index.
  const {activeStep, setActiveStep} = useSteps({
    index: UPLOAD_STEP,
    count: CREATE_NEW_PROJECT_STEPS.length,
  });

  const totalBilledMinutes: number = getTotalBilledMinutes(state);
  const availableCredits: number = balance?.unlimited ? Infinity : (balance?.balance ?? 0);

  const sourceLanguageName: string = Object.keys(state.subtitles.sourceLanguage)[0] || "";
  const isTranscribeOnly: boolean = Boolean(state.subtitles.isTranscribeOnly);
  // Transcribe-only submits no targets (useProjectUploader), so it is quoted as zero targets. The codes
  // are sorted because Rocky bills the first language by ISO code, whatever the selection order.
  const orderedTargetCodes: string[] = useMemo(
    () => isTranscribeOnly
      ? []
      : Object.keys(state.subtitles.targetLanguages).map((name: string) => iso6391.getCode(name) || name).sort(),
    [isTranscribeOnly, state.subtitles.targetLanguages]
  );
  const isDubbingScript: boolean = Boolean(state.subtitles.dubbingScript);
  const rates = catalog?.rates ?? DEFAULT_CREDIT_RATES;

  const breakdown: ICreditBreakdown = useMemo(
    () => buildCreditBreakdown({
      kind: "create",
      fileDurationsSeconds: Object.values(state.durationSecondsByFileName),
      orderedTargetLanguages: orderedTargetCodes,
      dubbingScript: isDubbingScript,
      rates,
    }),
    [state.durationSecondsByFileName, orderedTargetCodes, isDubbingScript, rates]
  );
  const projectCredits: ICreditQuote = breakdown.quote;

  const handleErrors: (error: string, action?: "add" | "remove") => void = useCallback(
    (error: string, action: "add" | "remove" = "add") => {
      if (state.errors.includes(error) && action == "add") return;
      if (action === "remove") {
        dispatch({type: "SET_ERRORS", payload: state.errors.filter((e) => e !== error)});
        return;
      }
      dispatch({type: "SET_ERRORS", payload: [...state.errors, error]});
    }, [dispatch, state.errors]);

  const isUploadStepValid = (): boolean => {
    const hasUploadedFiles: boolean = state.attachedFiles.length > 0;
    const hasDuration: boolean = totalBilledMinutes > 0;

    const allProjectNames: string[] = projects.map((p: IProject) => p.name);
    const hasProjectNameDuplicateError: boolean = state.attachedFiles.some((file: File) =>
      allProjectNames.includes(getFileBaseName(file.name))
    );
    if (hasProjectNameDuplicateError) {
      handleErrors(DUPLICATE_NAME_ERROR);
    } else if (state.errors.includes(DUPLICATE_NAME_ERROR)) {
      handleErrors(DUPLICATE_NAME_ERROR, "remove");
    }

    const hasEmptyNameError: boolean = state.attachedFiles.some((file: File) =>
      getFileBaseName(file.name) == ""
    );
    if (hasEmptyNameError) {
      handleErrors(EMPTY_NAME_ERROR);
    } else if (state.errors.includes(EMPTY_NAME_ERROR)) {
      handleErrors(EMPTY_NAME_ERROR, "remove");
    }

    return hasUploadedFiles && hasDuration && !hasProjectNameDuplicateError && !hasEmptyNameError;
  };

  // The budget is not part of this gate: Create stays clickable over budget and the fresh-balance
  // check in `handleCreate` answers with the not-enough dialog instead.
  const canCreateProject: boolean = state.errors.length === 0
    && isLanguagesStepValid(state.subtitles)
    && state.attachedFiles.length > 0;
  const canContinueToNextStep: boolean = activeStep === UPLOAD_STEP && isUploadStepValid();

  // The store balance can be stale (another member may have spent credits), so Create re-reads it
  // before closing the dialog; the not-enough dialog then opens above this still-mounted one.
  const handleCreate = async (): Promise<void> => {
    if (isCheckingCreditsRef.current) return;
    isCheckingCreditsRef.current = true;
    setIsCheckingCredits(true);
    try {
      const fresh = (workspaceId ? await fetchBalance(workspaceId) : null) ?? balance;
      if (!fresh?.unlimited && projectCredits.total > (fresh?.balance ?? 0)) {
        openNotEnough({requiredCredits: projectCredits.total, availableCredits: fresh?.balance ?? 0});
        return;
      }
      await createNewProject(state);
    } finally {
      isCheckingCreditsRef.current = false;
      setIsCheckingCredits(false);
    }
  };

  const goToStep = useCallback((stepIndex: number) => {
    const scrollArea: HTMLDivElement | null = stepScrollAreaRef.current;
    if (scrollArea !== null) {
      scrollArea.scrollTo({top: 0, behavior: "instant"});
      const dialog: Element | null = scrollArea.closest('[role="dialog"]');
      if (dialog instanceof HTMLElement) dialog.scrollTo({top: 0, behavior: "instant"});
    }
    setActiveStep(Math.min(Math.max(stepIndex, UPLOAD_STEP), LANGUAGES_STEP));
  }, [setActiveStep]);

  const goToSubtitleStep = useCallback((step: SubtitleStep): void => {
    setSubtitleInitialStep(step);
    goToStep(LANGUAGES_STEP);
  }, [goToStep]);

  const applyPreset = useCallback(
    (preset: SanitizedPreset) => dispatch({type: "APPLY_PRESET", payload: preset}),
    [dispatch]
  );

  return (
    <div className="flex h-[90vh] max-h-[calc(100vh-3rem)] min-h-0 flex-col p-12">
      <CreateProjectSteps
        steps={CREATE_NEW_PROJECT_STEPS}
        activeStep={activeStep}
      />
      <div
        ref={stepScrollAreaRef}
        className={`flex min-h-0 flex-1 flex-col ${
          activeStep === LANGUAGES_STEP ? "overflow-hidden" : "w-[43.75rem] self-center overflow-y-auto md:w-full"
        }`}
      >
        {activeStep === UPLOAD_STEP && (
          <StepUploadFiles
            uploadedFiles={state.attachedFiles}
            customConfigurationId={state.customConfigurationId}
            selectedSourceLanguage={sourceLanguageName}
            setUploadedFiles={(files: File[]) => dispatch({type: "SET_FILES", payload: files})}
            fileNameToSeriesNameRelation={state.fileNameToSeriesNameRelation}
            setFileNameToSeriesNameRelation={(relation: Record<string, string>) => dispatch({
              type: "SET_FILE_SERIES_RELATION",
              payload: relation
            })}
            setCustomConfiguration={(configId: string | undefined) => dispatch({
              type: "SET_CUSTOM_CONFIGURATION_ID",
              payload: configId
            })}
            applyPreset={applyPreset}
            goToSubtitleStep={goToSubtitleStep}
            remainingCredits={balance === null ? null : availableCredits}
            setFileDuration={(fileName: string, seconds: number) => dispatch({
              type: "SET_FILE_DURATION",
              payload: {fileName, seconds}
            })}
            renameFileDuration={(from: string, to: string) => dispatch({
              type: "RENAME_FILE_DURATION",
              payload: {from, to}
            })}
            removeFileDuration={(fileName: string) => dispatch({
              type: "REMOVE_FILE_DURATION",
              payload: {fileName}
            })}
            handleErrors={handleErrors}
          />
        )}
        {activeStep === LANGUAGES_STEP && (
          <LanguagesStep
            subtitlesState={state.subtitles}
            setSourceLanguage={(payload: Record<string, ILanguageConfiguration>) => dispatch({
              type: "SET_SOURCE_LANGUAGE",
              payload
            })}
            setTargetLanguages={(payload: Record<string, ILanguageConfiguration>) => dispatch({
              type: "SET_TARGET_LANGUAGES",
              payload
            })}
            markSubtitleSettingsDirty={() => dispatch({type: "MARK_SUBTITLE_SETTINGS_DIRTY"})}
            toggleIsTranscribeOnly={() => dispatch({type: "TOGGLE_TRANSCRIBE_ONLY"})}
            toggleKeepSongs={() => dispatch({type: "TOGGLE_KEEP_SONGS"})}
            setKeepSongs={(keepSongs: boolean) => dispatch({type: "SET_KEEP_SONGS", payload: keepSongs})}
            toggleDubbingScript={() => dispatch({type: "TOGGLE_DUBBING_SCRIPT"})}
            initialStep={subtitleInitialStep}
            onBack={() => goToStep(UPLOAD_STEP)}
            onCreate={() => void handleCreate()}
            isCreateDisabled={!canCreateProject}
            isCreating={isCheckingCredits}
            estimatedCredits={projectCredits.total}
            availableCredits={availableCredits}
            breakdown={breakdown}
            shouldSaveTemplate={state.shouldSaveTemplate}
            templateName={state.customConfigurationName}
            setShouldSaveTemplate={(shouldSave: boolean) => dispatch({
              type: "SET_SHOULD_SAVE_TEMPLATE",
              payload: shouldSave
            })}
            setTemplateName={(name: string) => dispatch({
              type: "SET_CUSTOM_CONFIGURATION_NAME",
              payload: name
            })}
          />
        )}
        {state.errors.length > 0 && (
          <div
            data-testid="create-project-step-errors"
            className="mt-4 max-h-20 shrink-0 overflow-y-auto rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3"
          >
            <ul className="list-disc pl-5 text-sm text-red-500 text-pretty">
              {state.errors.map((error: string, index: number) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
      {activeStep === UPLOAD_STEP && (
        <div className="mt-8 flex shrink-0 justify-end gap-3 border-t border-n-500 pt-6">
          <CreateProjectModalFooter
            activeStep={activeStep}
            finalStep={LANGUAGES_STEP}
            finalStepTitle="Create"
            canContinueToNextStep={canContinueToNextStep}
            isFinalStepDisabled={!canCreateProject}
            isFinalStepLoading={isCheckingCredits}
            goToNext={() => goToSubtitleStep("source")}
            goToPrevious={() => goToStep(UPLOAD_STEP)}
            onFinalStepClicked={() => void handleCreate()}
          />
        </div>
      )}
    </div>
  );
};

export default CreateNewProjectModal;
